import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, FileText, Image as ImageIcon, Music, Link as LinkIcon, Mic, Brain, BookOpen, Youtube, Bookmark, Sparkles, Search, Zap, CheckCircle2, Instagram, ChefHat, Clock, Users } from 'lucide-react';

// Play Store promo screenshots - rendered at a fixed 1080x1920 frame
// (9:16, the most common Play Store phone screenshot size). Each design
// is selected via ?n=1..5. Capture with Playwright at viewport 1080x1920
// to export the raw promo image.
//
// Design language matches the production AppLanding (cream bg + dark olive
// brand + mint emerald accent + chunky flying icons) so the install funnel
// feels continuous from store listing → first launch.

const FRAME_W = 1080;
const FRAME_H = 1920;

// Phone mockup reused across all screenshots, scaled for the promo canvas.
const PromoPhone = ({ children, rotate = -4, width = 540 }) => (
    <div
        className="rounded-[64px] p-3 shadow-[0_60px_120px_-30px_rgba(46,58,47,0.55)] mx-auto"
        style={{ background: '#1A1F1B', width, transform: `rotate(${rotate}deg)` }}
    >
        <div className="rounded-[52px] overflow-hidden bg-[#FDFCF8] aspect-[9/19] relative">
            <div className="absolute top-4 left-1/2 -translate-x-1/2 w-32 h-8 rounded-full bg-[#1A1F1B] z-10" />
            <div className="pt-20 px-5 h-full">{children}</div>
        </div>
    </div>
);

const HeaderRow = () => (
    <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 rounded-2xl bg-[#2E3A2F] flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
        </div>
        <div className="text-[20px] text-gray-500 font-semibold">All · 142</div>
    </div>
);

// ───── Screenshot 1: HERO ─────
const Hero = () => {
    const flyers = [
        { Icon: Play, x: -300, y: -360, color: '#EF4444' },
        { Icon: FileText, x: 320, y: -280, color: '#10b981' },
        { Icon: ImageIcon, x: -340, y: 60, color: '#A855F7' },
        { Icon: Music, x: 300, y: 220, color: '#F59E0B' },
        { Icon: LinkIcon, x: -200, y: -440, color: '#3B82F6' },
        { Icon: Mic, x: 240, y: 420, color: '#EC4899' },
        { Icon: BookOpen, x: -400, y: -120, color: '#14B8A6' },
    ];
    return (
        <div className="h-full flex flex-col items-center justify-between py-24 px-12">
            <div className="text-center">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white border border-emerald-200 text-emerald-700 text-[26px] font-semibold mb-12 shadow-sm">
                    <Sparkles className="w-7 h-7" /> Your AI second brain
                </div>
                <h1
                    className="font-heading font-black uppercase text-[#2E3A2F] leading-[0.88] tracking-tight"
                    style={{ fontSize: 150, letterSpacing: '-0.04em' }}
                >
                    Remember<br />
                    <span className="text-emerald-600">everything.</span>
                </h1>
                <p className="text-gray-600 text-[36px] mt-10 leading-snug max-w-[800px] mx-auto">
                    Save any link, reel, or post. BrainSync transcribes, summarizes, and organizes it - automatically.
                </p>
            </div>

            <div className="relative w-full flex items-center justify-center mt-16" style={{ minHeight: 700 }}>
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    {flyers.map(({ Icon, x, y, color }, i) => (
                        <div
                            key={i}
                            className="absolute w-[100px] h-[100px] rounded-3xl flex items-center justify-center shadow-[0_30px_60px_-12px_rgba(0,0,0,0.35)]"
                            style={{ background: color, transform: `translate(${x}px, ${y}px) rotate(${i % 2 === 0 ? -8 : 6}deg)` }}
                        >
                            <Icon className="w-12 h-12 text-white" strokeWidth={2.2} />
                        </div>
                    ))}
                </div>
                <PromoPhone width={500} rotate={-4}>
                    <HeaderRow />
                    <div className="bg-gray-100/80 rounded-full px-6 py-4 mb-5 text-[22px] text-gray-400">AI search…</div>
                    <PromoCard
                        icon={Youtube}
                        iconBg="#FEE2E2"
                        iconColor="#EF4444"
                        title="15-Minute Garlic Butter Pasta"
                        snippet="…boil pasta, sauté garlic, toss with cheese…"
                        tags={['recipe', 'pasta']}
                        sparkle
                        image="/play-store/pasta.png"
                    />
                    <div className="h-4" />
                    <PromoCard
                        icon={Bookmark}
                        iconBg="#FED7AA"
                        iconColor="#EA580C"
                        title="Building Your Second Brain"
                        snippet="…techniques for integrating digital notes…"
                        tags={['productivity']}
                        image="/play-store/notebook.png"
                    />
                </PromoPhone>
            </div>
        </div>
    );
};

const PromoCard = ({ icon: Icon, iconBg, iconColor, title, snippet, tags, sparkle, badge, image }) => (
    <div className="bg-white rounded-3xl shadow-[0_16px_40px_-16px_rgba(0,0,0,0.18)] border border-gray-100 p-5 relative">
        {sparkle && <Sparkles className="absolute top-3 right-4 w-6 h-6 text-amber-400" />}
        {badge && (
            <span className="absolute -top-3 left-4 bg-emerald-500 text-white text-[16px] font-bold px-3 py-1 rounded-full">
                {badge}
            </span>
        )}
        <div className="flex gap-4 items-start">
            {image ? (
                <div
                    className="w-20 h-20 rounded-2xl shrink-0 bg-gray-100 overflow-hidden"
                    style={{ backgroundImage: `url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                />
            ) : (
                <div className="w-20 h-20 rounded-2xl shrink-0 flex items-center justify-center" style={{ background: iconBg }}>
                    <Icon className="w-10 h-10" style={{ color: iconColor }} fill={iconColor} strokeWidth={1.5} />
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="text-[26px] font-bold text-[#1A1F1B] leading-tight">{title}</div>
                {snippet && <div className="text-[18px] text-gray-500 mt-1 leading-snug">{snippet}</div>}
                <div className="flex gap-2 mt-3 flex-wrap">
                    {tags.map((t) => (
                        <span key={t} className="text-[18px] font-medium px-3 py-1 rounded-lg bg-gray-100 text-gray-700">{t}</span>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

// ───── Screenshot 2: AI SUMMARIES ─────
const AISummaries = () => (
    <div className="h-full flex flex-col items-center justify-between pt-24 pb-20 px-16" style={{ background: 'linear-gradient(180deg, #FDFCF8 0%, #FDFCF8 60%, #DCFCE7 100%)' }}>
        <div className="text-center">
            <p className="text-[24px] font-mono uppercase tracking-[0.3em] text-emerald-700 mb-6">AI summaries</p>
            <h1 className="font-heading font-black text-[#2E3A2F] leading-[0.95] tracking-tight" style={{ fontSize: 108, letterSpacing: '-0.035em' }}>
                AI <span className="text-emerald-600">rebuilds</span> it<br />
                into something useful.
            </h1>
            <p className="text-gray-600 text-[32px] mt-8 leading-snug max-w-[820px] mx-auto">
                A recipe video becomes a recipe. An article becomes a summary. Automatically.
            </p>
        </div>
        <PromoPhone width={470} rotate={-3}>
            <HeaderRow />

            {/* Source video card (collapsed/origin) - compact, signals the
                "before" state of the AI transformation. */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 mb-3 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                    <Youtube className="w-6 h-6 text-red-500" fill="#EF4444" strokeWidth={0} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[18px] font-bold text-[#1A1F1B] truncate">15-Min Garlic Butter Pasta</div>
                    <div className="text-[14px] text-gray-500">youtube.com · 4:32</div>
                </div>
                <Sparkles className="w-6 h-6 text-emerald-500 shrink-0" />
            </div>

            {/* Rebuilt recipe card - full-width hero image + structured
                ingredients/steps. Text sizes bumped 50% over the prior
                pass for legibility at 1080×1920 zoomed-out Play Store
                preview. */}
            <div className="bg-white rounded-3xl shadow-[0_16px_40px_-16px_rgba(0,0,0,0.18)] border border-emerald-100 overflow-hidden">
                {/* Hero pasta photo */}
                <div
                    className="w-full h-40 bg-gray-100"
                    style={{ backgroundImage: "url(/play-store/pasta.png)", backgroundSize: 'cover', backgroundPosition: 'center' }}
                />

                <div className="px-5 pt-4 pb-3">
                    <div className="flex items-center gap-2.5 mb-1">
                        <div className="w-11 h-11 rounded-2xl bg-orange-100 flex items-center justify-center">
                            <ChefHat className="w-6 h-6 text-orange-600" strokeWidth={2} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-[24px] font-bold text-[#1A1F1B] leading-tight">Garlic Butter Pasta</div>
                            <div className="flex items-center gap-3 mt-0.5 text-[16px] text-gray-500">
                                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />15 min</span>
                                <span className="flex items-center gap-1"><Users className="w-4 h-4" />Serves 2</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-gray-100">
                    <div className="text-[15px] font-bold uppercase tracking-wide text-emerald-700 mb-2">Ingredients</div>
                    <ul className="space-y-1.5 text-[19px] text-gray-700">
                        <li className="flex gap-2"><span className="text-emerald-500">•</span>200g spaghetti</li>
                        <li className="flex gap-2"><span className="text-emerald-500">•</span>4 cloves garlic, minced</li>
                        <li className="flex gap-2"><span className="text-emerald-500">•</span>60g butter, parmesan, parsley</li>
                    </ul>
                </div>

                <div className="px-5 py-3 border-t border-gray-100">
                    <div className="text-[15px] font-bold uppercase tracking-wide text-emerald-700 mb-2">Steps</div>
                    <ol className="space-y-1.5 text-[19px] text-gray-700">
                        <li><span className="font-bold text-emerald-700 mr-1.5">1.</span>Boil pasta, save ½ cup water</li>
                        <li><span className="font-bold text-emerald-700 mr-1.5">2.</span>Sauté garlic in butter, 1 min</li>
                        <li><span className="font-bold text-emerald-700 mr-1.5">3.</span>Toss with pasta + cheese</li>
                    </ol>
                </div>

                <div className="px-5 py-3 border-t border-gray-100 flex gap-2 flex-wrap">
                    <span className="text-[15px] font-medium px-2.5 py-1 rounded-lg bg-orange-50 text-orange-700">recipe</span>
                    <span className="text-[15px] font-medium px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700">pasta</span>
                    <span className="text-[15px] font-medium px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700">15-min</span>
                </div>
            </div>
        </PromoPhone>
    </div>
);

// ───── Screenshot 3: MULTI-FORMAT ─────
const MultiFormat = () => {
    const platforms = [
        { Icon: Youtube, name: 'YouTube', bg: '#FEE2E2', color: '#EF4444' },
        { Icon: Instagram, name: 'Instagram', bg: '#FCE7F3', color: '#DB2777' },
        { Icon: FileText, name: 'Articles', bg: '#DBEAFE', color: '#2563EB' },
        { Icon: Play, name: 'TikTok', bg: '#E0E7FF', color: '#4F46E5' },
        { Icon: ImageIcon, name: 'Photos', bg: '#FED7AA', color: '#EA580C' },
        { Icon: Mic, name: 'Podcasts', bg: '#E9D5FF', color: '#9333EA' },
    ];
    return (
        <div className="h-full flex flex-col items-center justify-center py-24 px-16">
            <div className="text-center mb-16">
                <p className="text-[24px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-6">Universal</p>
                <h1 className="font-heading font-black text-[#2E3A2F] leading-[0.95] tracking-tight" style={{ fontSize: 130, letterSpacing: '-0.035em' }}>
                    Save <span className="text-emerald-600">anything.</span><br />
                    From anywhere.
                </h1>
                <p className="text-gray-600 text-[34px] mt-10 leading-snug max-w-[800px] mx-auto">
                    Share into BrainSync from any app. Links, videos, photos, voice memos - it all lands in one searchable second brain.
                </p>
            </div>
            <div className="grid grid-cols-2 gap-8 w-full max-w-[820px]">
                {platforms.map((p) => (
                    <div key={p.name} className="bg-white rounded-3xl p-8 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.18)] border border-gray-100 flex items-center gap-5">
                        <div className="w-24 h-24 rounded-3xl flex items-center justify-center" style={{ background: p.bg }}>
                            <p.Icon className="w-12 h-12" style={{ color: p.color }} fill={p.color} strokeWidth={1.5} />
                        </div>
                        <div>
                            <div className="text-[34px] font-bold text-[#1A1F1B]">{p.name}</div>
                            <div className="text-[20px] text-gray-500">auto-summarized</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ───── Screenshot 4: AI SEARCH ─────
const AISearch = () => (
    <div className="h-full flex flex-col items-center justify-between py-28 px-16">
        <div className="text-center">
            <p className="text-[24px] font-mono uppercase tracking-[0.3em] text-emerald-700 mb-6">AI search</p>
            <h1 className="font-heading font-black text-[#2E3A2F] leading-[0.95] tracking-tight" style={{ fontSize: 130, letterSpacing: '-0.035em' }}>
                Find anything<br />
                <span className="text-emerald-600">by meaning.</span>
            </h1>
            <p className="text-gray-600 text-[34px] mt-10 leading-snug max-w-[800px] mx-auto">
                Don't remember the title? Ask in plain English. BrainSync understands.
            </p>
        </div>
        <PromoPhone width={520} rotate={-3}>
            <HeaderRow />

            {/* "Ask BrainSync" header */}
            <div className="flex items-center justify-center gap-2.5 mb-4">
                <div className="w-9 h-9 rounded-2xl bg-emerald-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
                </div>
                <span className="text-[26px] font-heading font-black text-[#1A1F1B] tracking-tight">
                    Ask <span className="text-emerald-600">BrainSync</span>
                </span>
            </div>

            {/* Search bar */}
            <div className="bg-white border-2 border-emerald-400 rounded-full px-5 py-4 mb-4 flex items-center gap-3 shadow-[0_8px_24px_-8px_rgba(16,185,129,0.4)]">
                <Search className="w-7 h-7 text-emerald-700 shrink-0" />
                <span className="text-[20px] text-emerald-900 font-semibold truncate">that podcast about sleep</span>
            </div>

            {/* AI answer bubble */}
            <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-2xl p-4 mb-4 flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
                    <Brain className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-bold uppercase tracking-wide text-emerald-700 mb-1">BrainSync</div>
                    <p className="text-[17px] text-gray-700 leading-snug">
                        Found <span className="font-bold text-[#1A1F1B]">3 saves</span> about sleep. The Andrew Huberman episode on deep-sleep protocols matches best.
                    </p>
                </div>
            </div>

            {/* Top result card */}
            <PromoCard
                icon={Mic}
                iconBg="#E9D5FF"
                iconColor="#9333EA"
                title="Deep Sleep Protocols"
                snippet="…temperature, light exposure, and consistent timing…"
                tags={['sleep', 'health']}
                image="/play-store/sleep-scene.png"
            />
        </PromoPhone>
    </div>
);

// ───── Screenshot 5: FREE FOREVER ─────
const FreeForever = () => (
    <div className="h-full flex flex-col items-center justify-center py-28 px-16" style={{ background: 'radial-gradient(ellipse 90% 70% at 50% 50%, #2E3A2F 0%, #1F2A21 100%)' }}>
        <div className="text-center">
            <p className="text-[24px] font-mono uppercase tracking-[0.3em] text-emerald-300 mb-8">Free forever</p>
            <h1 className="font-heading font-black uppercase text-white leading-[0.88] tracking-tight" style={{ fontSize: 150, letterSpacing: '-0.04em' }}>
                Unlimited<br />
                <span className="text-emerald-300">bookmarks.</span>
            </h1>
            <p className="text-white/70 text-[36px] mt-10 leading-snug max-w-[800px] mx-auto">
                Save as many as you want - forever. 20 free AI saves to get you started.
            </p>
        </div>
        <div className="mt-20 grid grid-cols-1 gap-6 w-full max-w-[760px]">
            {[
                'Unlimited bookmark storage',
                '20 free AI summaries',
                'Smart search across everything',
                'Works on every device',
                'No credit card required',
            ].map((feature) => (
                <div key={feature} className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-2xl px-8 py-6 flex items-center gap-5">
                    <div className="w-14 h-14 rounded-full bg-emerald-400/20 border border-emerald-400/40 flex items-center justify-center shrink-0">
                        <CheckCircle2 className="w-7 h-7 text-emerald-300" />
                    </div>
                    <span className="text-[30px] font-semibold text-white">{feature}</span>
                </div>
            ))}
        </div>
        <div className="mt-16 bg-emerald-400 text-[#1F2A21] rounded-full px-12 py-7 flex items-center gap-4 shadow-[0_30px_70px_-15px_rgba(110,231,183,0.6)]">
            <Zap className="w-9 h-9" fill="#1F2A21" />
            <span className="text-[36px] font-black tracking-tight">Get BrainSync free</span>
        </div>
    </div>
);

const PlayScreenshots = () => {
    const [params] = useSearchParams();
    const n = parseInt(params.get('n') || '1', 10);

    const screens = { 1: Hero, 2: AISummaries, 3: MultiFormat, 4: AISearch, 5: FreeForever };
    const Component = screens[n] || Hero;

    return (
        <div className="bg-black flex items-start justify-center" style={{ minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="relative overflow-hidden"
                style={{
                    width: FRAME_W,
                    height: FRAME_H,
                    background: 'radial-gradient(ellipse 80% 60% at 50% 0%, #DCFCE7 0%, transparent 60%), #FDFCF8',
                }}
                data-testid={`play-screenshot-${n}`}
            >
                {/* Grain */}
                <div
                    className="absolute inset-0 pointer-events-none opacity-[0.04] mix-blend-multiply"
                    style={{
                        backgroundImage:
                            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                    }}
                />
                <Component />
            </motion.div>
        </div>
    );
};

export default PlayScreenshots;
